import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";
import { DateFilter, buildParams } from "@/components/DateFilter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, TrendingDown, Wallet, BookOpen } from "lucide-react";

function Stat({ label, value, tint }) {
  return (
    <Card className="p-4 border-slate-200">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-xl font-bold font-mono mt-1 ${tint}`}>{money(value)}</div>
    </Card>
  );
}

export default function DayBook() {
  const [filter, setFilter] = useState({ period: "today" });
  const { data } = useQuery({ queryKey: ["daybook", filter], queryFn: async () => (await api.get("/daybook", { params: buildParams(filter) })).data });
  const d = data || {};

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">DayBook</h2><p className="text-slate-500 text-sm">Daily cash flow & activity</p></div>
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Stat label="Sales" value={d.sales_total} tint="text-emerald-600" />
        <Stat label="Purchases" value={d.purchase_total} tint="text-blue-600" />
        <Stat label="Expenses" value={d.expense_total} tint="text-rose-600" />
        <Stat label="Credit Given" value={d.credit} tint="text-amber-600" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <Card className="p-4 border-slate-200 flex items-center gap-3"><div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-emerald-600" /></div><div><div className="text-xs text-slate-400">Cash Received</div><div className="font-mono font-bold text-lg text-emerald-600">{money(d.cash_received)}</div></div></Card>
        <Card className="p-4 border-slate-200 flex items-center gap-3"><div className="h-11 w-11 rounded-xl bg-rose-50 flex items-center justify-center"><TrendingDown className="h-5 w-5 text-rose-600" /></div><div><div className="text-xs text-slate-400">Cash Paid</div><div className="font-mono font-bold text-lg text-rose-600">{money(d.cash_paid)}</div></div></Card>
        <Card className="p-4 border-slate-200 flex items-center gap-3"><div className="h-11 w-11 rounded-xl bg-[#1A2A5E]/10 flex items-center justify-center"><Wallet className="h-5 w-5 text-[#1A2A5E]" /></div><div><div className="text-xs text-slate-400">Net Balance</div><div className="font-mono font-bold text-lg text-[#1A2A5E]">{money(d.balance)}</div></div></Card>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100"><h3 className="font-display font-semibold text-[#0F172A]">Transactions</h3></div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Time</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead><TableHead>Details</TableHead><TableHead>Method</TableHead><TableHead className="text-right">In</TableHead><TableHead className="text-right">Out</TableHead></TableRow></TableHeader>
            <TableBody>
              {(d.entries || []).length ? d.entries.map((e, i) => (
                <TableRow key={i} className="hover:bg-slate-50">
                  <TableCell className="text-slate-500 text-sm">{fmtDateTime(e.time)}</TableCell>
                  <TableCell><Badge className={e.type === "Sale" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : e.type === "Purchase" ? "bg-blue-50 text-blue-700 hover:bg-blue-50" : "bg-rose-50 text-rose-700 hover:bg-rose-50"}>{e.type}</Badge></TableCell>
                  <TableCell className="font-mono text-sm">{e.ref || "—"}</TableCell>
                  <TableCell className="text-slate-600">{e.desc || "—"}</TableCell>
                  <TableCell className="capitalize text-slate-400 text-sm">{e.method || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-600">{e.in ? money(e.in) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-rose-600">{e.out ? money(e.out) : "—"}</TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400"><BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />No activity for this period</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
